import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  SelectedTab,
  selectIsAdvancedSearchOpen,
  selectIsQuizFeedbackOpen,
  selectIsValidationOverlayOpen,
  selectSelectedTab,
  setIsAdvancedSearchOpen,
  setIsValidationOverlayOpen,
} from "../../app/slices/GlobalSlice";
import {
  selectIsDirty,
  selectIsPlaying,
  selectTakes3D,
  selectTrainingType,
  TrainingType,
} from "../../app/slices/TrainingSlice";
import PiConfirmationDialog from "../../components/PiConfirmationDialog";
import { useLoadTraining } from "../../hooks/LoadTrainingHook";
import OnboardingHost from "../onboarding/OnboardingHost";
import ShortcutsOverlay from "../onboarding/shortcuts/ShortcutsOverlay";
import PropertiesColumn from "../properties_column/PropertiesColumn";
import TrainingActionbar from "../training_actionbar/TrainingActionbar";
import {
  TrainingEditor360,
  TrainingEditor3D,
  TrainingEditorNotSet,
} from "../training_preview/TrainingEditorStrategy";
import Preview from "../training_preview/TrainingPreview";
import TrainingTimeline from "../training_timeline/TrainingTimeline";
import { CopyPasteProvider } from "../training_timeline/CopyPasteContext";
import "./TrainingEditor.css";
import useValidation from "../validation/Validation.hooks";
import useErrorValidation from "../validation/ErrorValidation.hook";
import AdvancedSearchOverlay from "../advanced_search_column/AdvancedSearchOverlay";
import ValidationOverlay from "../validation/ValidationOverlay";
import { useTrainingAutoSave } from "../../hooks/UseTrainingAutoSaveHook";
import ScenarioBuilderColumn from "../scenario_builder_3d/ScenarioBuilderColumn";
import QuizFeedbackCollapsableColumn from "../quiz_feedback/QuizFeedbackCollapsableColumn";
import QuizFeedbackCollapsedRail from "../quiz_feedback/QuizFeedbackCollapsedRail";
import {
  QUIZ_FEEDBACK_FULL_COLUMN_WIDTH,
  QUIZ_FEEDBACK_RAIL_WIDTH,
  QUIZ_FEEDBACK_SPLIT_COLUMN_WIDTH,
  QUIZ_FEEDBACK_TRANSITION,
} from "../quiz_feedback/QuizFeedbackLayout";
import ScenarioBuilderTabRail from "../scenario_builder_3d/ScenarioBuilderTabRail";
import StoryboardTimeline3D from "../timeline_3d/StoryboardTimeline3D";
import { StoryboardTakeCardView } from "../timeline_3d/StoryboardTakeCard3D";
import TrainingEditor3DEmpty from "./TrainingEditor3DEmpty";
import useShortcut, { ShortCuts } from "../../hooks/UseShortcutsHook";
import ProjectSettingsWorkspace from "../project_settings/ProjectSettingsWorkspace";
import TrainingEditor360Rail from "./TrainingEditor360Rail";
import TakeInteractionDndProvider, {
  GroupDragOverlayBadge,
} from "../training_timeline/TakeInteractionDndProvider";
import TimelineClipboardProvider from "../training_timeline/TimelineClipboardProvider";
import { EditorInteractionRowView } from "../editor_columns/EditorInteractionRow";

export default function TrainingEditor() {
  const dispatch = useDispatch();
  const isDirty = useSelector(selectIsDirty);
  const { t } = useTranslation();
  const { trainingId } = useParams();
  const [loadTraining] = useLoadTraining();
  const navigate = useNavigate();
  const [errorDialogOpen, setErrorDialogOpen] = useState<boolean>(false);
  const trainingType = useSelector(selectTrainingType);
  const selectedTab = useSelector(selectSelectedTab);
  const isPlaying = useSelector(selectIsPlaying);
  const isAdvancedSearchOpen = useSelector(selectIsAdvancedSearchOpen);
  const isValidationOverlayOpen = useSelector(selectIsValidationOverlayOpen);
  const isQuizFeedbackOpen = useSelector(selectIsQuizFeedbackOpen);
  const takes3D = useSelector(selectTakes3D);
  const trainingStrategy = useMemo(() => {
    if (trainingType === TrainingType.Training3D) return TrainingEditor3D;
    else if (trainingType === TrainingType.Training360) return TrainingEditor360;
    else return TrainingEditorNotSet;
  }, [trainingType]);
  const show3DEmptyHero =
    trainingType === TrainingType.Training3D &&
    !isPlaying &&
    takes3D.length === 0;
  const show3DStoryboardTimeline =
    !isPlaying &&
    selectedTab === SelectedTab.Timeline &&
    !show3DEmptyHero;
  const show3DProjectSettings =
    trainingType === TrainingType.Training3D &&
    !isPlaying &&
    selectedTab === SelectedTab.ProjectSettings;
  const show3DPropertiesColumn =
    trainingType === TrainingType.Training3D &&
    !isPlaying &&
    selectedTab === SelectedTab.Timeline;
  const storyboardColumnWidth = isQuizFeedbackOpen
    ? QUIZ_FEEDBACK_RAIL_WIDTH
    : QUIZ_FEEDBACK_FULL_COLUMN_WIDTH;
  const scenarioBuilderColumnWidth = isQuizFeedbackOpen
    ? QUIZ_FEEDBACK_SPLIT_COLUMN_WIDTH
    : QUIZ_FEEDBACK_FULL_COLUMN_WIDTH;

  // ========
  // ** Validation Hooks **
  // ** don't delete :) **
  useValidation();
  useErrorValidation();
  useTrainingAutoSave();
  // ========

  const openAdvancedSearch = useCallback(() => {
    if (trainingType !== TrainingType.NotSet && !isPlaying) {
      dispatch(setIsAdvancedSearchOpen(true));
    }
  }, [dispatch, isPlaying, trainingType]);

  const closeAdvancedSearch = useCallback(() => {
    dispatch(setIsAdvancedSearchOpen(false));
  }, [dispatch]);

  const closeValidationOverlay = useCallback(() => {
    dispatch(setIsValidationOverlayOpen(false));
  }, [dispatch]);

  useShortcut(ShortCuts.Search, openAdvancedSearch);

  const handleBeforeUnload = (event: BeforeUnloadEvent) => {
    if (isDirty) {
      event.preventDefault();
      event.returnValue =
        "All unsaved changes will be lost. Are you sure you want to leave?";
    }
  };

  const LoadTrainingPage = async () => {
    if (!trainingId) return;

    var loadSuccesful = await loadTraining(trainingId);
    if (!loadSuccesful) {
      setErrorDialogOpen(true);
    }
  };

  const ExitEditorOnError = () => {
    navigate("/");
  };

  useEffect(() => {
    LoadTrainingPage();
  }, [trainingId]);

  useEffect(() => {
    if (trainingType === TrainingType.NotSet || isPlaying) {
      dispatch(setIsAdvancedSearchOpen(false));
      dispatch(setIsValidationOverlayOpen(false));
    }
  }, [dispatch, isPlaying, trainingType]);

  useEffect(() => {
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  });

  const editorContent =
    trainingType === TrainingType.Training3D ? (
      <div className="relative trainingeditor-content trainingeditor-content-3d">
        <TakeInteractionDndProvider
          takes={takes3D}
          renderOverlay={(activeDrag) => {
            if (!activeDrag) return null;
            if (activeDrag.kind === "interaction") {
              return (
                <GroupDragOverlayBadge count={activeDrag.interactions.length}>
                  <EditorInteractionRowView
                    interaction={activeDrag.interaction}
                    ghost
                    testIdPrefix="scenario-row-interaction"
                  />
                </GroupDragOverlayBadge>
              );
            }

            return (
              <StoryboardTakeCardView
                take={activeDrag.take}
                index={takes3D.findIndex(
                  (take) => take.id === activeDrag.take.id
                )}
                isSelected={false}
                isGhost
                issueSummary={{
                  warningCount: 0,
                  errorCount: 0,
                  hasWarnings: false,
                  hasErrors: false,
                }}
              />
            );
          }}
        >
          <TimelineClipboardProvider>
          <div className="trainingeditor-3d-body">
          {!isPlaying && <ScenarioBuilderTabRail />}
          {show3DProjectSettings ? (
            <ProjectSettingsWorkspace />
          ) : show3DEmptyHero ? (
            <TrainingEditor3DEmpty />
          ) : (
            <>
              {/* the storyboard collapses to a slim rail while quiz feedback is open */}
              {show3DStoryboardTimeline && (
                <div
                  className="h-full overflow-hidden"
                  style={{
                    width: storyboardColumnWidth,
                    minWidth: storyboardColumnWidth,
                    transition: QUIZ_FEEDBACK_TRANSITION,
                  }}
                >
                  {isQuizFeedbackOpen ? (
                    <QuizFeedbackCollapsedRail />
                  ) : (
                    <StoryboardTimeline3D />
                  )}
                </div>
              )}
              {!isPlaying && (
                <div
                  className="h-full overflow-hidden"
                  style={{
                    width: scenarioBuilderColumnWidth,
                    minWidth: scenarioBuilderColumnWidth,
                    transition: QUIZ_FEEDBACK_TRANSITION,
                  }}
                >
                  <ScenarioBuilderColumn />
                </div>
              )}
              {!isPlaying && <QuizFeedbackCollapsableColumn />}
              <div className="trainingeditor-3d-center">
                <div className="trainingeditor-3d-preview">
                  <Preview isVisible={true} strategy={trainingStrategy} />
                </div>
              </div>
              {show3DPropertiesColumn && (
                <div className="trainingeditor-3d-properties">
                  <PropertiesColumn />
                </div>
              )}
            </>
          )}
          </div>
          </TimelineClipboardProvider>
        </TakeInteractionDndProvider>
        {!isPlaying && (
          <>
            <AdvancedSearchOverlay
              isOpen={isAdvancedSearchOpen}
              onClose={closeAdvancedSearch}
            />
            <ValidationOverlay
              isOpen={isValidationOverlayOpen}
              onClose={closeValidationOverlay}
            />
          </>
        )}
      </div>
    ) : (
      <div className="relative trainingeditor-content">
        {!isPlaying && <TrainingEditor360Rail />}
        <TrainingTimeline strategy={trainingStrategy} />
        <Preview
          isVisible={selectedTab === SelectedTab.Timeline}
          strategy={trainingStrategy}
        />
        {!isPlaying && <PropertiesColumn />}
        {!isPlaying && (
          <>
            <AdvancedSearchOverlay
              isOpen={isAdvancedSearchOpen}
              onClose={closeAdvancedSearch}
            />
            <ValidationOverlay
              isOpen={isValidationOverlayOpen}
              onClose={closeValidationOverlay}
            />
          </>
        )}
      </div>
    );

  return (
    <>
      <div className="h-screen max-h-screen relative overflow-y-clip w-[100%] min-w-[1200px]">
        <CopyPasteProvider>
          <TrainingActionbar />
          {editorContent}
        </CopyPasteProvider>
      </div>

      {/*
       * Withheld until the training type is known.
       *
       * clearTraining runs at the start of every load (LoadTrainingHook.tsx:48)
       * and leaves trainingType NotSet, which useOnboardingStrategy maps to the
       * dashboard strategy. Mounting unconditionally therefore started the
       * dashboard tour inside the editor, where none of its three anchors
       * exist: the runner would abort it and remember that for the session,
       * costing the author a tour they never saw. The mirror of why the host
       * lives on DashboardPage rather than the shared Dashboard layout.
       */}
      {trainingType !== TrainingType.NotSet && <OnboardingHost />}
      <ShortcutsOverlay />
      <PiConfirmationDialog
        open={errorDialogOpen}
        onClose={() => setErrorDialogOpen(false)}
        title={t("load_training_failed.title")}
        text={t("load_training_failed.description") + " " + trainingId}
        onConfirm={ExitEditorOnError}
        confirmText={t("load_training_failed.confirm")}
        cancelText=""
        confirmButtonVariant="piRed"
        hideCancelButton
        height="200px"
      />
    </>
  );
}
